/**
 * Application-wide date presentation.
 *
 * Every user-visible date — web pages and generated PDF reports alike —
 * renders as DD-MMM-YYYY (e.g. 09-AUG-2026).
 *
 * The month is spelled, not numbered, so the value cannot be misread
 * across locales: 09-08-2026 is the 9th of August to a European reader
 * and the 8th of September to an American one, while 09-AUG-2026 is
 * unambiguous everywhere. That matters as the product reaches more
 * countries, and it matters on a clinical report.
 *
 * Scope: presentation only. Values sent to APIs, used in Ditto RQL
 * filters, bound to native <input type="date">, or used in filenames
 * stay in their machine formats (ISO / YYYY-MM-DD) — do not route those
 * through here.
 */
import moment from "moment";

export const APP_DATE_FORMAT = "DD-MMM-YYYY";

// Shapes a date can arrive in from Ditto, eCRF records and legacy
// fields. Order matters: MM/DD/YYYY is tried before DD/MM/YYYY, which is
// what the previous per-page formatters did — keep it that way so no
// existing value changes meaning.
const PARSE_FORMATS = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "DD-MM-YYYY",
  "DD/MMM/YYYY",
  "DD-MMM-YYYY",
  "MMM DD, YYYY",
];

/**
 * Coerce anything date-ish to a moment, or null if it can't be read.
 * Accepts moments, Dates, epoch millis and the string shapes above.
 */
export const toMoment = (value) => {
  if (value == null || value === "") return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  if (value instanceof Date || typeof value === "number") {
    const m = moment(value);
    return m.isValid() ? m : null;
  }
  const s = String(value).trim();
  if (!s) return null;
  let m = moment(s, PARSE_FORMATS, true);
  if (!m.isValid()) m = moment(s); // lenient fallback for odd inputs
  return m.isValid() ? m : null;
};

/**
 * Format as DD-MMM-YYYY with an upper-case month (09-AUG-2026).
 * Returns "" for anything unreadable, so callers can render it directly.
 */
export const formatAppDate = (value) => {
  const m = toMoment(value);
  return m ? m.format(APP_DATE_FORMAT).toUpperCase() : "";
};

/**
 * Same date, followed by a time. `time` is a moment format string and
 * `separator` sits between the two parts.
 */
export const formatAppDateTime = (value, { time = "HH:mm:ss", separator = " " } = {}) => {
  const m = toMoment(value);
  if (!m) return "";
  return m.format(APP_DATE_FORMAT).toUpperCase() + separator + m.format(time);
};
